import { Button } from '@/components/ui/button';
import { PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { NewChatButton } from './NewChatButton';
import { useTranslation } from 'react-i18next';

export interface ChatHistoryItemType {
  id: string;
  title: string;
  isActive?: boolean;
}

export interface ChatSidebarAction {
  key: string;
  label: string;
  icon: React.ElementType;
  onClick: () => void;
}

interface ChatSidebarProps<T extends ChatHistoryItemType = ChatHistoryItemType> {
  conversations: T[];
  renderHistoryItem: (conversation: T) => React.ReactNode;
  onNewConversation: () => void;
  actions?: ChatSidebarAction[];
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

export function ChatSidebar<T extends ChatHistoryItemType = ChatHistoryItemType>({
  conversations,
  renderHistoryItem,
  onNewConversation,
  actions,
  isCollapsed,
  onToggleCollapse,
}: ChatSidebarProps<T>) {
  const { t } = useTranslation('prince');
  return (
    <div
      className={`flex flex-col shadow-md overflow-y-auto overflow-x-hidden transition-all duration-200 flex-shrink-0 ${
        isCollapsed ? 'w-14 p-2' : 'w-72 sm:w-[15rem] p-3'
      }`}
    >
      <div className={`flex ${isCollapsed ? 'flex-col gap-2' : 'items-center gap-2'}`}>
        {isCollapsed ? (
          <>
            <Button
              onClick={onToggleCollapse}
              variant="ghost"
              size="icon"
              className="w-full h-8 flex-shrink-0"
              data-testid="chat-sidebar-collapse-button"
              aria-label={t('sidebar.expand')}
            >
              <PanelLeftOpen className="w-4 h-4" />
            </Button>
            <NewChatButton onClick={onNewConversation} collapsed={isCollapsed} />
          </>
        ) : (
          <>
            <NewChatButton onClick={onNewConversation} collapsed={isCollapsed} />
            <Button
              onClick={onToggleCollapse}
              variant="ghost"
              size="icon"
              className="w-8 h-8 flex-shrink-0"
              data-testid="chat-sidebar-collapse-button"
              aria-label={t('sidebar.collapse')}
            >
              <PanelLeftClose className="w-4 h-4" />
            </Button>
          </>
        )}
      </div>

      {!isCollapsed && (
        <>
          {/* Actions */}
          {actions && actions.length > 0 && (
            <div className="space-y-1 mt-2">
              {actions.map(action => {
                const Icon = action.icon;
                return (
                  <Button
                    key={action.key}
                    onClick={action.onClick}
                    variant="outline"
                    className="w-full bg-white border-none rounded-sm !justify-start flex gap-2 !px-2 !py-2.5"
                    data-testid={`chat-sidebar-action-${action.key}`}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="truncate">{action.label}</span>
                  </Button>
                );
              })}
            </div>
          )}

          {/* Chat History */}
          <div className="flex-1 mt-2">{conversations.map(chat => renderHistoryItem(chat))}</div>
        </>
      )}
    </div>
  );
}
